import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';

// ─── Types ───

export type PatientProfileSection =
  | 'Personal Info'
  | 'Contact'
  | 'Emergency Contact'
  | 'Medication'
  | 'Intake Form';

export type PatientProfileField = {
  key: string;
  label: string;
  section: PatientProfileSection;
  visible: boolean;
  required?: boolean;
  locked?: boolean;
};

export type PatientProfileOverride = {
  key: string;
  visible?: boolean;
  label?: string;
  required?: boolean;
};

// ─── Base schema (defaults) ───

const BASE_PATIENT_PROFILE_FIELDS: PatientProfileField[] = [
  // ─── Personal Info ───
  {
    key: 'patient.full_name',
    label: 'Patient Name',
    section: 'Personal Info',
    visible: true,
    locked: true,
  },
  {
    key: 'patient.patient_number',
    label: 'Patient Number',
    section: 'Personal Info',
    visible: true,
    locked: true,
  },
  { key: 'patient.id_type', label: 'ID Type', section: 'Personal Info', visible: true },
  { key: 'patient.id_number', label: 'ID Number', section: 'Personal Info', visible: true },
  { key: 'patient.sex', label: 'Gender', section: 'Personal Info', visible: true },
  { key: 'patient.birth_date', label: 'Date of Birth', section: 'Personal Info', visible: true },
  { key: 'patient.age', label: 'Age', section: 'Personal Info', visible: false },
  {
    key: 'patient.marital_status',
    label: 'Marital Status',
    section: 'Personal Info',
    visible: false,
  },
  { key: 'patient.blood_group', label: 'Blood Group', section: 'Personal Info', visible: false },
  { key: 'patient.nationality', label: 'Nationality', section: 'Personal Info', visible: false },
  { key: 'patient.occupation', label: 'Occupation', section: 'Personal Info', visible: false },
  { key: 'patient.mobile_number', label: 'Mobile Number', section: 'Personal Info', visible: true },

  // ─── Contact ───
  { key: 'contact.email', label: 'Email', section: 'Contact', visible: true },
  {
    key: 'contact.residential_address',
    label: 'Address (Residential/Present)',
    section: 'Contact',
    visible: true,
  },
  { key: 'contact.alternate_phone', label: 'Alternate Phone', section: 'Contact', visible: false },
  {
    key: 'contact.permanent_address',
    label: 'Permanent Address',
    section: 'Contact',
    visible: false,
  },

  // ─── Emergency Contact ───
  {
    key: 'emergency.name',
    label: 'Emergency Contact Name',
    section: 'Emergency Contact',
    visible: false,
  },
  {
    key: 'emergency.relationship',
    label: 'Relationship',
    section: 'Emergency Contact',
    visible: false,
  },
  {
    key: 'emergency.phone',
    label: 'Emergency Phone',
    section: 'Emergency Contact',
    visible: false,
  },

  // ─── Medication ───
  { key: 'medication.allergies', label: 'Allergies', section: 'Medication', visible: true },
  {
    key: 'medication.current_medications',
    label: 'Current Medications',
    section: 'Medication',
    visible: false,
  },
  {
    key: 'medication.medical_history',
    label: 'Medical History',
    section: 'Medication',
    visible: false,
  },
  {
    key: 'medication.chronic_conditions',
    label: 'Chronic Conditions',
    section: 'Medication',
    visible: false,
  },

  // ─── Intake Form ───
  { key: 'intake.smoking_status', label: 'Smoking Status', section: 'Intake Form', visible: false },
  {
    key: 'intake.alcohol_consumption',
    label: 'Alcohol Consumption',
    section: 'Intake Form',
    visible: false,
  },
  {
    key: 'intake.exercise_frequency',
    label: 'Exercise Frequency',
    section: 'Intake Form',
    visible: false,
  },
  {
    key: 'intake.dietary_restrictions',
    label: 'Dietary Restrictions',
    section: 'Intake Form',
    visible: false,
  },
];

// ─── Resolver ───

export function resolvePatientProfileFields(
  base: PatientProfileField[],
  overrides: Record<string, PatientProfileOverride>,
): PatientProfileField[] {
  return base.map((field) => {
    const override = overrides[field.key];
    if (!override) return field;
    return {
      ...field,
      label: override.label ?? field.label,
      required: override.required ?? field.required,
      visible: field.locked ? field.visible : (override.visible ?? field.visible),
    };
  });
}

// ─── Section chip colors ───

const SECTION_STYLES: Record<PatientProfileSection, { bg: string; color: string }> = {
  'Personal Info': { bg: 'var(--color-info-bg)', color: 'var(--color-info)' },
  Contact: { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' },
  'Emergency Contact': { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' },
  Medication: { bg: 'var(--color-danger-bg)', color: 'var(--color-danger-text)' },
  'Intake Form': { bg: 'var(--color-neutral-bg)', color: 'var(--text-muted)' },
};

// ─── Icons ───

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function StethoscopeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
      <circle cx="20" cy="10" r="2" />
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7v6h6" />
      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
    </svg>
  );
}

// ─── Toggle ───

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onChange}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: disabled ? 'var(--border)' : checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 150ms ease',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          transition: 'transform 150ms ease',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          display: 'block',
        }}
      />
    </button>
  );
}

// ─── Section Chip ───

function SectionChip({ section }: { section: PatientProfileSection }) {
  const s = SECTION_STYLES[section];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 var(--sp-2)',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '22px',
        letterSpacing: '0.12px',
        whiteSpace: 'nowrap',
      }}
    >
      {section}
    </span>
  );
}

// ─── Status Chip (Default / Overridden) ───

function StatusChip({ isOverridden }: { isOverridden: boolean }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        padding: '0 6px',
        borderRadius: 'var(--radius-full)',
        background: isOverridden ? 'var(--color-warning-bg)' : 'var(--bg-surface-2)',
        color: isOverridden ? 'var(--color-warning-text)' : 'var(--text-muted)',
        fontSize: 11,
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
        letterSpacing: '0.12px',
        whiteSpace: 'nowrap',
      }}
    >
      {isOverridden ? 'Overridden' : 'Default'}
    </span>
  );
}

// ─── Card wrapper ───

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

// ─── Confirm Dialog ───

function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          padding: 'var(--sp-6)',
          width: 400,
          maxWidth: '90vw',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            color: 'var(--text)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            lineHeight: 'var(--line-height-body)',
            color: 'var(--text-muted)',
            marginTop: 'var(--sp-2)',
          }}
        >
          {body}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-3)',
            marginTop: 'var(--sp-6)',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              height: 36,
              padding: '0 var(--sp-4)',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              height: 36,
              padding: '0 var(--sp-4)',
              borderRadius: 'var(--input-radius)',
              border: 'none',
              background: 'var(--color-danger)',
              color: '#ffffff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Patient Profile Card ───

function PatientProfileCard({
  showVitals,
  onToggleVitals,
  onManageVitals,
}: {
  showVitals: boolean;
  onToggleVitals: () => void;
  onManageVitals: () => void;
}) {
  return (
    <Card>
      {/* Card header */}
      <div style={{ padding: 'var(--sp-6)' }}>
        <div
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-lg)',
            color: 'var(--text)',
          }}
        >
          Patient Profile
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--text-muted)',
            marginTop: 'var(--sp-1)',
          }}
        >
          Control what information is displayed in patient profiles
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--border)' }} />

      {/* Toggle row */}
      <div
        style={{
          padding: 'var(--sp-5) var(--sp-6)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--sp-4)',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text)',
            }}
          >
            Show Vitals section in Patient Profile
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              lineHeight: 'var(--line-height-body)',
              color: 'var(--text-muted)',
              marginTop: 'var(--sp-1)',
              maxWidth: 480,
            }}
          >
            Controls visibility of the Vitals card in the patient profile view. When enabled, vital
            signs data will be displayed for all patient profiles.
          </div>
        </div>
        <Toggle checked={showVitals} onChange={onToggleVitals} />
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--border)' }} />

      {/* Action row */}
      <div
        onClick={onManageVitals}
        style={{
          padding: 'var(--sp-4) var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          cursor: 'pointer',
          transition: 'background 150ms ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-surface-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: 'var(--text-muted)',
          }}
        >
          <StethoscopeIcon />
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text)',
            }}
          >
            Manage vitals definitions
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--text-muted)',
              marginTop: 2,
            }}
          >
            Configure vital signs, units, and validation rules
          </div>
        </div>
        <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
          <ChevronRightIcon />
        </span>
      </div>
    </Card>
  );
}

// ─── Visible Fields Card ───

interface ResolvedRow {
  field: PatientProfileField;
  isOverridden: boolean;
}

function VisibleFieldsCard({
  rows,
  search,
  onSearchChange,
  onToggleField,
  onResetField,
  onShowAll,
  onReset,
  totalCount,
  totalVisibleCount,
  overrideCount,
}: {
  rows: ResolvedRow[];
  search: string;
  onSearchChange: (v: string) => void;
  onToggleField: (key: string) => void;
  onResetField: (key: string) => void;
  onShowAll: () => void;
  onReset: () => void;
  totalCount: number;
  totalVisibleCount: number;
  overrideCount: number;
}) {
  const colTemplate = '1fr 140px 100px 160px';

  return (
    <Card>
      {/* Card header with search */}
      <div
        style={{
          padding: 'var(--sp-6)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--sp-4)',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-lg)',
              color: 'var(--text)',
            }}
          >
            Visible fields on Patient Profile
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--text-muted)',
              marginTop: 'var(--sp-1)',
            }}
          >
            Choose which patient fields appear on the Patient Profile screen
          </div>
        </div>

        {/* Search input */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            height: 36,
            padding: '0 12px',
            border: '1px solid var(--border-input)',
            borderRadius: 'var(--input-radius)',
            background: 'var(--input-bg)',
            width: 200,
            flexShrink: 0,
          }}
        >
          <span style={{ color: 'var(--text-muted)', display: 'flex', flexShrink: 0 }}>
            <SearchIcon />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search fields..."
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              fontFamily: 'var(--font-family)',
              outline: 'none',
              minWidth: 0,
            }}
          />
        </div>
      </div>

      {/* Table header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: colTemplate,
          padding: '0 var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          borderTop: '1px solid var(--border)',
          height: 36,
          alignItems: 'center',
        }}
      >
        <span style={colHeaderStyle}>Field</span>
        <span style={colHeaderStyle}>Section</span>
        <span style={colHeaderStyle}>Status</span>
        <span style={{ ...colHeaderStyle, textAlign: 'right' }}>Visible on Profile</span>
      </div>

      {/* Table rows */}
      {rows.length === 0 ? (
        <div
          style={{
            padding: 'var(--sp-8) var(--sp-6)',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-base)',
          }}
        >
          No fields match your search.
        </div>
      ) : (
        rows.map(({ field, isOverridden }) => (
          <div
            key={field.key}
            style={{
              display: 'grid',
              gridTemplateColumns: colTemplate,
              padding: '0 var(--sp-6)',
              borderBottom: '1px solid var(--border)',
              height: 48,
              alignItems: 'center',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--text)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              {field.label}
            </span>
            <SectionChip section={field.section} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <StatusChip isOverridden={isOverridden} />
              {isOverridden && (
                <button
                  onClick={() => onResetField(field.key)}
                  title="Reset to default"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    padding: 0,
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <UndoIcon />
                </button>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Toggle
                checked={field.visible}
                onChange={() => onToggleField(field.key)}
                disabled={field.locked}
              />
            </div>
          </div>
        ))
      )}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--sp-3) var(--sp-6)',
          borderTop: rows.length === 0 ? undefined : 'none',
        }}
      >
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          Showing {rows.length} of {totalCount} fields &bull; {totalVisibleCount} visible on profile
          {overrideCount > 0 && <> &bull; {overrideCount} overridden</>}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
          {overrideCount > 0 && (
            <button
              onClick={onReset}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                padding: 0,
                fontFamily: 'var(--font-family)',
              }}
            >
              Reset to defaults
            </button>
          )}
          {search && (
            <button
              onClick={onShowAll}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-primary)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                padding: 0,
                fontFamily: 'var(--font-family)',
              }}
            >
              Show all fields
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Additional Settings Card ───

function AdditionalSettingsCard() {
  return (
    <div
      style={{
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-8) var(--sp-6)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        Additional patient settings
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          marginTop: 'var(--sp-1)',
        }}
      >
        More configuration options coming soon
      </div>
    </div>
  );
}

// ─── Main Page ───

export function PatientProfileSettingsPage() {
  useOutletContext<{ collapsed: boolean }>();
  const navigate = useNavigate();

  const [showVitals, setShowVitals] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  // Overrides state (simulated — future: load from server)
  const [overrides, setOverrides] = useState<Record<string, PatientProfileOverride>>({});

  // Resolve base + overrides
  const resolvedFields = useMemo(
    () => resolvePatientProfileFields(BASE_PATIENT_PROFILE_FIELDS, overrides),
    [overrides],
  );

  const overrideCount = Object.keys(overrides).length;

  // Build rows with override indicator
  const allRows: ResolvedRow[] = useMemo(
    () =>
      resolvedFields.map((field) => ({
        field,
        isOverridden: field.key in overrides,
      })),
    [resolvedFields, overrides],
  );

  // Apply search filter (by label + key)
  const filteredRows = useMemo(() => {
    if (!search.trim()) return allRows;
    const q = search.toLowerCase().trim();
    return allRows.filter(
      (r) => r.field.label.toLowerCase().includes(q) || r.field.key.toLowerCase().includes(q),
    );
  }, [allRows, search]);

  const totalVisibleCount = useMemo(
    () => resolvedFields.filter((f) => f.visible).length,
    [resolvedFields],
  );

  // Toggle: create or remove override so DB stays clean
  const toggleField = useCallback((key: string) => {
    const baseField = BASE_PATIENT_PROFILE_FIELDS.find((f) => f.key === key);
    if (!baseField || baseField.locked) return;

    setOverrides((prev) => {
      const existing = prev[key];
      const currentVisible = existing?.visible ?? baseField.visible;
      const newVisible = !currentVisible;

      // If toggling back to base value, remove the override entry
      if (newVisible === baseField.visible) {
        return Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key));
      }

      return { ...prev, [key]: { ...existing, key, visible: newVisible } };
    });
  }, []);

  // Per-row reset
  const resetField = useCallback((key: string) => {
    setOverrides((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key)));
  }, []);

  // Global reset (with confirm dialog)
  function handleResetConfirm() {
    setOverrides({});
    setConfirmResetOpen(false);
  }

  function handleSave() {
    // TODO: persist { showVitals, overrides } to server
    console.info('[PatientProfileSettings] save', { showVitals, overrides });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '20px var(--sp-6)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
            <h1
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-xl)',
                letterSpacing: '-0.44px',
                color: 'var(--text)',
                margin: 0,
              }}
            >
              Patient Profile
            </h1>
            <span
              style={{
                height: 22,
                padding: '0 var(--sp-2)',
                borderRadius: 'var(--radius-xs)',
                background: 'var(--color-warning-bg)',
                color: 'var(--color-warning-text)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
                lineHeight: '22px',
                letterSpacing: '0.12px',
              }}
            >
              Admin only
            </span>
          </div>
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text-muted)',
              margin: '4px 0 0',
            }}
          >
            Configure patient profile and data display settings
          </p>
        </div>

        {/* TODO: wire to persistence layer */}
        <Button variant="gradient" size="md" onClick={handleSave}>
          Save
        </Button>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 'var(--sp-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-6)',
        }}
      >
        {/* Patient Profile card */}
        <PatientProfileCard
          showVitals={showVitals}
          onToggleVitals={() => setShowVitals((v) => !v)}
          onManageVitals={() => navigate('/clinic/vitals-definitions')}
        />

        {/* Visible fields table card */}
        <VisibleFieldsCard
          rows={filteredRows}
          search={search}
          onSearchChange={setSearch}
          onToggleField={toggleField}
          onResetField={resetField}
          onShowAll={() => setSearch('')}
          onReset={() => setConfirmResetOpen(true)}
          totalCount={resolvedFields.length}
          totalVisibleCount={totalVisibleCount}
          overrideCount={overrideCount}
        />

        {/* Footer note */}
        <div
          style={{
            textAlign: 'center',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            marginTop: 'calc(-1 * var(--sp-3))',
          }}
        >
          These settings affect all patients in this clinic.
        </div>

        {/* Additional settings placeholder */}
        <AdditionalSettingsCard />
      </div>

      {/* Reset confirm dialog */}
      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset to defaults?"
        body="This will remove all custom settings and restore the default patient profile fields."
        confirmLabel="Reset"
        onConfirm={handleResetConfirm}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
}

// ─── Shared styles ───

const colHeaderStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)' as React.CSSProperties['fontWeight'],
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};
